import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Car, Star, CheckCircle, Clock, Eye, ChevronRight, Search, Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

export default function JudgeDashboard() {
  const navigate = useNavigate();
  const { currentEvent, events, switchEvent } = useEvent();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cars, setCars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scores, setScores] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [scoringForm, setScoringForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [quickPickOpen, setQuickPickOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(0);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (currentEvent && user) {
      fetchCars();
      fetchCategories();
      fetchMyScores();
    }
  }, [currentEvent, user]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars?event_id=${currentEvent.id}&approved_only=true`);
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
      toast.error('Failed to load cars');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?event_id=${currentEvent.id}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMyScores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scores?event_id=${currentEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only my scores
      const myScores = response.data.filter(s => s.judge_id === user.id);
      setScores(myScores);
    } catch (error) {
      console.error('Error fetching scores:', error);
    }
  };

  const getCarScores = (carId) => {
    return scores.filter(s => s.car_id === carId);
  };

  const isCarFullyScored = (carId) => {
    const carScores = getCarScores(carId);
    return categories.length > 0 && carScores.length >= categories.length;
  };

  const getCarTotalScore = (carId) => {
    const carScores = getCarScores(carId);
    return carScores.reduce((sum, s) => sum + s.score, 0);
  };

  const openScoringModal = (car) => {
    setSelectedCar(car);
    setUploadedPhoto(null); // Reset uploaded photo when opening modal
    
    // Pre-fill with existing scores only
    const existingScores = {};
    const carScores = getCarScores(car.id);
    carScores.forEach(s => {
      existingScores[s.category_id] = { score: s.score, notes: s.notes || '', scored: true };
    });
    
    // Initialize unscored categories with null score (requires input)
    categories.forEach(cat => {
      if (!existingScores[cat.id]) {
        existingScores[cat.id] = { score: null, notes: '', scored: false };
      }
    });
    
    setScoringForm(existingScores);
    setShowScoringModal(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedPhoto(event.target.result);
      toast.success('Photo ready - will be saved with your scores');
    };
    reader.readAsDataURL(file);
  };

  const handleScoreChange = (categoryId, value) => {
    const numValue = Math.min(10, Math.max(0, parseInt(value) || 0));
    setScoringForm({
      ...scoringForm,
      [categoryId]: { ...scoringForm[categoryId], score: numValue, scored: true }
    });
  };

  const handleNotesChange = (categoryId, value) => {
    setScoringForm({
      ...scoringForm,
      [categoryId]: { ...scoringForm[categoryId], notes: value }
    });
  };

  const getUnscoredCategories = () => {
    return categories.filter(cat => {
      const formData = scoringForm[cat.id];
      return !formData || formData.score === null || !formData.scored;
    });
  };

  const scrollToCategory = (categoryId) => {
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('ring-2', 'ring-red-500');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-red-500');
      }, 2000);
    }
  };

  const handleSubmitScores = async () => {
    // Validate all categories have been scored
    const unscored = getUnscoredCategories();
    if (unscored.length > 0) {
      toast.error(`Please score all categories. Missing: ${unscored.map(c => c.name).join(', ')}`);
      // Scroll to first unscored category
      scrollToCategory(unscored[0].id);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // If there's an uploaded photo that hasn't been saved yet, save it first
      if (uploadedPhoto && selectedCar) {
        try {
          const photoResponse = await axios.put(`${API}/cars/${selectedCar.id}/photo`, {
            photo_data: uploadedPhoto
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local car data with the response from server
          const updatedCarData = photoResponse.data;
          setCars(cars.map(car => 
            car.id === selectedCar.id ? { ...car, photo_data: updatedCarData.photo_data } : car
          ));
        } catch (photoError) {
          console.error('Error saving photo:', photoError);
          // Continue with scoring even if photo fails
          toast.warning('Photo could not be saved, but scores will still be submitted');
        }
      }
      
      // Submit each category score
      for (const categoryId of Object.keys(scoringForm)) {
        const scoreData = {
          car_id: selectedCar.id,
          category_id: categoryId,
          score: scoringForm[categoryId].score,
          notes: scoringForm[categoryId].notes || null
        };
        
        await axios.post(`${API}/scores?event_id=${currentEvent.id}`, scoreData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(uploadedPhoto ? 'Scores and photo saved successfully!' : 'Scores submitted successfully!');
      setShowScoringModal(false);
      setUploadedPhoto(null);
      fetchMyScores();
    } catch (error) {
      console.error('Error submitting scores:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit scores');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Filter cars based on search query (registration number)
  const filterCars = (carList) => {
    if (!searchQuery.trim()) return carList;
    const query = searchQuery.trim().toLowerCase().replace(/^#/, '');
    return carList.filter(car => 
      car.registration_number.toLowerCase().includes(query) ||
      car.make.toLowerCase().includes(query) ||
      car.model.toLowerCase().includes(query) ||
      car.owner_name.toLowerCase().includes(query)
    );
  };

  const scoredCars = filterCars(cars.filter(c => isCarFullyScored(c.id)));
  const pendingCars = filterCars(cars.filter(c => !isCarFullyScored(c.id)));

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3 min-w-0">
              <Logo />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Judge Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Welcome, {user?.name}</p>
              </div>
            </div>
            
            {/* Event selector and Logout */}
            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
              {events && events.length > 1 ? (
                <Select
                  value={currentEvent?.id || ''}
                  onValueChange={(eventId) => {
                    const event = events.find(e => e.id === eventId);
                    if (event) {
                      switchEvent(event);
                      toast.success(`Switched to ${event.name}`);
                    }
                  }}
                >
                  <SelectTrigger className="w-[160px] sm:w-[250px] bg-white border-gray-300 text-gray-900 text-sm">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : currentEvent && (
                <span className="text-gray-700 text-xs sm:text-sm font-medium truncate max-w-[150px] sm:max-w-none">
                  Event: {currentEvent.name}
                </span>
              )}
              <Button 
                onClick={handleLogout} 
                variant="outline" 
                size="sm"
                className="border-pink-500 hover:bg-pink-50 whitespace-nowrap [&>svg]:text-pink-600"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline text-pink-600">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title and Search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 font-bold mb-2">Car Scoring</h2>
            <p className="text-gray-600">Score each car across all categories</p>
          </div>
          
          {/* Search Box — also acts as a quick-pick autocomplete: typing
              shows the top matches; pressing Enter or clicking a row jumps
              straight into scoring that car (no scrolling). */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-600" />
            <Input
              type="text"
              placeholder="Search or jump to car # (e.g., 023)"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setQuickPickOpen(true); setHighlightedIdx(0); }}
              onFocus={() => setQuickPickOpen(true)}
              onBlur={() => setTimeout(() => setQuickPickOpen(false), 150)}
              onKeyDown={(e) => {
                const quickList = filterCars(cars).slice(0, 8);
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIdx((i) => Math.min(i + 1, quickList.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIdx((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const target = quickList[highlightedIdx] || quickList[0];
                  if (target) {
                    setQuickPickOpen(false);
                    setSearchQuery('');
                    openScoringModal(target);
                  }
                } else if (e.key === 'Escape') {
                  setQuickPickOpen(false);
                }
              }}
              className="pl-10 bg-gray-100 border-gray-300 text-gray-900 placeholder:text-gray-600 focus:border-pink-500"
              data-testid="judge-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setQuickPickOpen(false); }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900"
              >
                ✕
              </button>
            )}
            {quickPickOpen && searchQuery.trim() && (() => {
              const quickList = filterCars(cars).slice(0, 8);
              if (quickList.length === 0) return null;
              return (
                <div
                  className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-auto"
                  data-testid="judge-quick-pick-dropdown"
                >
                  {quickList.map((car, idx) => {
                    const done = isCarFullyScored(car.id);
                    const isActive = idx === highlightedIdx;
                    return (
                      <button
                        type="button"
                        key={car.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setQuickPickOpen(false);
                          setSearchQuery('');
                          openScoringModal(car);
                        }}
                        onMouseEnter={() => setHighlightedIdx(idx)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-gray-100 last:border-b-0 ${isActive ? 'bg-pink-50' : 'bg-white hover:bg-gray-50'}`}
                        data-testid={`judge-quick-pick-row-${car.registration_number}`}
                      >
                        <span className="font-mono font-bold text-pink-700 w-12">#{car.registration_number}</span>
                        <span className="flex-1 min-w-0 truncate text-gray-900">
                          {car.year} {car.make} {car.model}
                          <span className="text-gray-600 text-sm"> — {car.owner_name}</span>
                        </span>
                        {done ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                  <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
                    ↑↓ navigate · Enter to score · Esc to close
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-6 p-3 bg-white border border-gray-200 shadow-sm rounded-lg rounded-lg">
            <p className="text-gray-600">
              Showing results for "<span className="text-red-400 font-medium">{searchQuery}</span>"
              {' '}- {pendingCars.length + scoredCars.length} car(s) found
              {pendingCars.length + scoredCars.length === 0 && (
                <span className="text-gray-600 ml-2">Try searching by registration number, make, model, or owner name</span>
              )}
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Cars</p>
                <p className="text-3xl font-bold text-gray-900">{cars.length}</p>
              </div>
              <Car className="w-12 h-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Scored</p>
                <p className="text-3xl font-bold text-green-500">{cars.filter(c => isCarFullyScored(c.id)).length}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pending</p>
                <p className="text-3xl font-bold text-yellow-500">{cars.filter(c => !isCarFullyScored(c.id)).length}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Categories Info */}
        {categories.length === 0 ? (
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 rounded-lg text-center mb-8">
            <Star className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
            <h3 className="text-2xl font-bold mb-2">No Scoring Categories</h3>
            <p className="text-gray-600">
              Scoring categories have not been set up for this event yet. 
              Please contact an administrator.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4 rounded-lg mb-8">
            <p className="text-gray-600 text-sm">
              <strong className="text-gray-900">Scoring Categories:</strong>{' '}
              {categories.map(c => c.name).join(', ')}
              <span className="text-gray-600 ml-2">({categories.length} categories, max {categories.length * 10} points)</span>
            </p>
          </div>
        )}

        {/* Cars to Score */}
        {cars.length === 0 ? (
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-12 rounded-lg text-center">
            <Car className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-2xl font-bold mb-2">No Cars to Score</h3>
            <p className="text-gray-600">
              There are no approved cars in this event yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pending Cars */}
            {pendingCars.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  <Clock className="w-5 h-5 inline mr-2 text-yellow-500" />
                  Needs Scoring ({pendingCars.length})
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {pendingCars.map(car => (
                    <div key={car.id} className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 rounded-lg hover:bg-gray-50 transition-colors">
                      {car.photo_data ? (
                        <img
                          src={car.photo_data}
                          alt={`${car.make} ${car.model}`}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center mb-4">
                          <Car className="h-16 w-16 text-gray-600" />
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold px-3 py-1 rounded-full bg-pink-600/20 text-red-400">
                          #{car.registration_number}
                        </span>
                        <span className="text-sm text-gray-600">
                          {getCarScores(car.id).length}/{categories.length} scored
                        </span>
                      </div>
                      
                      <h4 className="text-xl font-bold text-gray-900 mb-1">
                        {car.year} {car.make} {car.model}
                      </h4>
                      <p className="text-gray-600 text-sm mb-1">{car.class_name}</p>
                      <p className="text-gray-600 text-sm mb-2">Owner: {car.owner_name}</p>
                      
                      {/* Check-in Status */}
                      <div className="mb-4">
                        {car.checked_in ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Checked In
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
                            <Clock className="w-3 h-3 mr-1" />
                            Not Checked In
                          </span>
                        )}
                      </div>
                      
                      <Button 
                        onClick={() => openScoringModal(car)}
                        className="w-full bg-pink-600 hover:bg-pink-700"
                        disabled={categories.length === 0}
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Score This Car
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Scored Cars */}
            {scoredCars.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  <CheckCircle className="w-5 h-5 inline mr-2 text-green-500" />
                  Already Scored ({scoredCars.length})
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {scoredCars.map(car => (
                    <div key={car.id} className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 rounded-lg opacity-75">
                      {car.photo_data ? (
                        <img
                          src={car.photo_data}
                          alt={`${car.make} ${car.model}`}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center mb-4">
                          <Car className="h-16 w-16 text-gray-600" />
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold px-3 py-1 rounded-full bg-green-600/20 text-green-400">
                          #{car.registration_number}
                        </span>
                        <span className="text-sm text-green-400 font-bold">
                          {getCarTotalScore(car.id)} pts
                        </span>
                      </div>
                      
                      <h4 className="text-xl font-bold text-gray-900 mb-1">
                        {car.year} {car.make} {car.model}
                      </h4>
                      <p className="text-gray-600 text-sm mb-1">{car.class_name}</p>
                      <p className="text-gray-600 text-sm mb-2">Owner: {car.owner_name}</p>
                      
                      {/* Check-in Status */}
                      <div className="mb-4">
                        {car.checked_in ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Checked In
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
                            <Clock className="w-3 h-3 mr-1" />
                            Not Checked In
                          </span>
                        )}
                      </div>
                      
                      <Button 
                        onClick={() => openScoringModal(car)}
                        variant="outline"
                        className="w-full border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View / Edit Scores
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Scoring Modal */}
      <Dialog open={showScoringModal} onOpenChange={setShowScoringModal}>
        <DialogContent className="max-w-2xl bg-white text-gray-900 border-gray-300 max-h-[90vh] overflow-y-auto">
          {selectedCar && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-gray-900 font-bold">
                  Score: {selectedCar.year} {selectedCar.make} {selectedCar.model}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Registration #{selectedCar.registration_number} • {selectedCar.class_name}
                </DialogDescription>
              </DialogHeader>
              
              {/* Photo Section */}
              <div className="mb-4">
                {selectedCar.photo_data || uploadedPhoto ? (
                  <div className="relative">
                    <img
                      src={uploadedPhoto || selectedCar.photo_data}
                      alt={`${selectedCar.make} ${selectedCar.model}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    {uploadedPhoto && (
                      <div className="absolute top-2 right-2 flex gap-2">
                        <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                          Photo will be saved with scores
                        </span>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setUploadedPhoto(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {!uploadedPhoto && (
                      <label className="absolute bottom-2 right-2 cursor-pointer">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-white/90 hover:bg-white"
                          asChild
                        >
                          <span>
                            <Camera className="w-4 h-4 mr-1" />
                            Change Photo
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                    <Camera className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600 text-sm mb-3">No photo for this car yet</p>
                    <label className="cursor-pointer">
                      <Button variant="outline" className="border-pink-500 text-pink-600 hover:bg-pink-50" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Photo will be saved when you submit scores</p>
                  </div>
                )}
              </div>

              {/* Scoring Progress */}
              {categories.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Scoring Progress: {categories.filter(c => scoringForm[c.id]?.scored).length} / {categories.length} categories
                    </span>
                    {getUnscoredCategories().length > 0 && (
                      <span className="text-yellow-400">
                        ⚠ {getUnscoredCategories().length} unscored
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex space-x-1">
                    {categories.map(cat => (
                      <div
                        key={cat.id}
                        className={`flex-1 h-2 rounded ${
                          scoringForm[cat.id]?.scored ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                        title={`${cat.name}: ${scoringForm[cat.id]?.scored ? 'Scored' : 'Not scored'}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {categories.map(category => {
                  const isUnscored = !scoringForm[category.id]?.scored;
                  return (
                    <div 
                      key={category.id} 
                      id={`category-${category.id}`}
                      className={`border rounded-lg p-4 transition-all ${
                        isUnscored 
                          ? 'border-yellow-500/50 bg-yellow-500/5' 
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {isUnscored && (
                            <span className="text-yellow-400 text-sm">●</span>
                          )}
                          <div>
                            <Label className="text-gray-900 text-lg font-medium">{category.name}</Label>
                            {category.description && (
                              <p className="text-gray-600 text-sm">{category.description}</p>
                            )}
                            {isUnscored && (
                              <p className="text-yellow-400 text-xs mt-1">Score required</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="0"
                            max="10"
                            value={scoringForm[category.id]?.score ?? ''}
                            placeholder="—"
                            onChange={(e) => handleScoreChange(category.id, e.target.value)}
                            className={`w-20 text-center text-xl font-bold border-gray-300 text-gray-900 ${
                              isUnscored ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-gray-100'
                            }`}
                          />
                          <span className="text-gray-600">/10</span>
                        </div>
                      </div>
                      
                      {/* Score slider visualization - 10 segments for 1-10, 0 = no segments lit */}
                      <div className="flex space-x-1 mb-3">
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <button
                            key={n}
                            onClick={() => handleScoreChange(category.id, n)}
                            className={`flex-1 h-3 rounded ${
                              scoringForm[category.id]?.scored && n <= scoringForm[category.id]?.score
                                ? 'bg-red-500'
                                : 'bg-gray-700'
                            } transition-colors hover:bg-red-400`}
                            title={`Score: ${n}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <button
                          onClick={() => handleScoreChange(category.id, 0)}
                          className={`text-xs px-2 py-1 rounded ${
                            scoringForm[category.id]?.scored && scoringForm[category.id]?.score === 0
                              ? 'bg-gray-600 text-gray-900'
                              : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
                          }`}
                        >
                          N/A (0)
                        </button>
                        <p className="text-xs text-gray-600">Click N/A if category cannot be scored</p>
                      </div>
                      
                      <Input
                        placeholder="Optional notes for this category..."
                        value={scoringForm[category.id]?.notes || ''}
                        onChange={(e) => handleNotesChange(category.id, e.target.value)}
                        className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-600"
                      />
                    </div>
                  );
                })}
              </div>

              {categories.length > 0 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <span className="text-gray-600">Total Score:</span>
                    <span className="text-2xl font-bold text-gray-900 ml-2">
                      {Object.values(scoringForm).reduce((sum, s) => sum + (s.scored ? (s.score || 0) : 0), 0)}
                    </span>
                    <span className="text-gray-600">/{categories.length * 10}</span>
                  </div>
                </div>
              )}

              <DialogFooter className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowScoringModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitScores}
                  disabled={submitting || categories.length === 0 || getUnscoredCategories().length > 0}
                  className={`${
                    getUnscoredCategories().length > 0 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-pink-600 hover:bg-pink-700'
                  }`}
                >
                  {submitting ? 'Submitting...' : 
                    getUnscoredCategories().length > 0 
                      ? `Score ${getUnscoredCategories().length} More Categories` 
                      : uploadedPhoto 
                        ? 'Submit Scores & Save Photo'
                        : 'Submit Scores'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
